import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { gkCategories } from "@/mocks/categories";

const navMain = [
  { to: "/", label: "今日新瓜" },
  { to: "/news", label: "新鲜瓜速递" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [q, setQ] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = q.trim();
    navigate(kw ? `/news?q=${encodeURIComponent(kw)}` : "/news");
  };

  return (
    <header className={`sticky top-0 z-50 transition-colors duration-300 ${scrolled ? "bg-background-50/95 backdrop-blur border-b border-background-200" : "bg-background-50"}`}>
      <div className="mx-auto flex h-16 items-center justify-between gap-4 px-4 md:px-8">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap" aria-label="看吃瓜首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-50">
            <i className="ri-water-flash-fill text-lg"></i>
          </span>
          <span className="font-heading text-2xl leading-none text-primary-600">看吃瓜</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-6 md:flex" aria-label="主导航">
          <Link to="/news" className="hidden whitespace-nowrap text-sm text-foreground-700 hover:text-primary-600 lg:block">
            {navMain[1].label}
          </Link>
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-1 whitespace-nowrap text-sm text-foreground-700 hover:text-primary-600"
            >
              栏目瓜田
              <i className={`ri-arrow-down-s-line transition-transform ${menuOpen ? "rotate-180" : ""}`}></i>
            </button>
            {menuOpen && (
              <div className="absolute left-0 top-full mt-3 w-56 rounded-xl border border-background-200 bg-background-50 p-2 shadow-soft">
                {gkCategories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-foreground-700 hover:bg-background-100"
                  >
                    <i className={`${c.icon} text-primary-500`}></i>
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <Link to="/" className="hidden whitespace-nowrap text-sm text-foreground-700 hover:text-primary-600 lg:block">
            {navMain[0].label}
          </Link>
          <Link to="/about" className="whitespace-nowrap text-sm text-foreground-700 hover:text-primary-600">
            关于我们
          </Link>
          <Link to="/faq" className="whitespace-nowrap text-sm text-foreground-700 hover:text-primary-600">
            常见问题
          </Link>
        </nav>

        {/* Search + actions */}
        <div className="flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden items-center gap-2 rounded-full border border-background-200 bg-background-50 px-3 py-2 md:flex">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="搜今日新瓜…"
              className="w-36 bg-transparent text-sm outline-none placeholder:text-foreground-400"
            />
            <button type="submit" aria-label="搜索" className="text-foreground-500 hover:text-primary-600">
              <i className="ri-search-line"></i>
            </button>
          </form>
          <Link to="/" className="hidden rounded-full bg-primary-500 px-5 py-2 text-sm whitespace-nowrap text-background-50 hover:bg-primary-600 sm:block">
            吃今日的瓜
          </Link>
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-background-200 text-foreground-700 md:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="打开菜单"
            aria-expanded={mobileOpen}
          >
            <i className={mobileOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"}></i>
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <nav className="border-t border-background-200 bg-background-50 px-4 py-4 md:hidden" aria-label="移动端导航">
          <form onSubmit={submitSearch} className="mb-4 flex items-center gap-2 rounded-full border border-background-200 px-3 py-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜今日新瓜…" className="flex-1 bg-transparent text-sm outline-none placeholder:text-foreground-400" />
            <button type="submit" aria-label="搜索" className="text-foreground-500">
              <i className="ri-search-line"></i>
            </button>
          </form>
          <div className="flex flex-col gap-1">
            {navMain.map((n) => (
              <Link key={n.label} to={n.to} className="rounded-lg px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100">
                {n.label}
              </Link>
            ))}
            <p className="mt-2 px-3 text-xs font-medium uppercase tracking-wide text-foreground-400">栏目瓜田</p>
            {gkCategories.map((c) => (
              <Link key={c.slug} to={`/category/${c.slug}`} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground-700 hover:bg-background-100">
                <i className={`${c.icon} text-primary-500`}></i>
                {c.name}
              </Link>
            ))}
            <Link to="/about" className="rounded-lg px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100">
              关于我们
            </Link>
            <Link to="/faq" className="rounded-lg px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100">
              常见问题
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}